<template>
    <div ref="variantsWrapper">
        <div
            v-for="(variantGroup, index) in variants"
            :key="variantGroup.id"
            class="mb-6"
            :role="isSelectGroup(variantGroup) || isSingleTextOptionGroup(variantGroup) ? undefined : 'radiogroup'"
            :aria-labelledby="isSelectGroup(variantGroup) || isSingleTextOptionGroup(variantGroup) ? undefined : `groupLabel_${variantGroup.id}`"
        >
            <div v-if="getTranslatedProperty(variantGroup, 'name')" class="mb-3 leading-4 text-sm font-semibold flex items-center gap-1">
                <span
                    v-if="variants.length >= 4"
                    class="inline-flex w-5 h-5 justify-center items-center border rounded-full text-xs"
                    :class="[ groupStyles(variantGroup) ]"
                >{{ index + 1 }}</span>
                <span :id="`groupLabel_${variantGroup.id}`">{{ getTranslatedProperty(variantGroup, 'name') }}:</span>
                <!-- display name of selected option if set and option group contains media (swatches), or the sole option of a single-option text group -->
                <span v-if="isSwatchGroup(variantGroup)">
                    {{ getTranslatedProperty(variantGroup?.options?.find((option) => option.id === selectedOptions[variantGroup.id]), 'name') }}
                </span>
                <span v-else-if="isSingleTextOptionGroup(variantGroup)">
                    {{ getTranslatedProperty(getSelectableOptions(variantGroup.options)[0]!, 'name') }}
                </span>
            </div>

            <MiscSelect
                v-if="isSelectGroup(variantGroup) && !isSingleTextOptionGroup(variantGroup)"
                :model-value="selectedOptions[variantGroup.id]"
                :options="getSelectOptions(variantGroup)"
                :placeholder="$t('product.detail.variants.selectPlaceholder', { group: variantGroup.name })"
                :disabled="deactivatedGroups.includes(variantGroup.id)"
                :aria-labelledby="`groupLabel_${variantGroup.id}`"
                data-cy="variant-select"
                @update:model-value="onSelectChange(index, variantGroup, $event)"
            />
            <div v-else-if="!isSingleTextOptionGroup(variantGroup)" class="relative flex flex-wrap gap-3">
                <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                <label
                    v-for="(variantOption, optionIndex) in getSelectableOptions(variantGroup.options)"
                    :key="variantOption.id"
                    :class="[
                        { 'hidden': isGroupCollapsed(variantGroup.id) && (optionIndex + 1) * 82 > variantsWrapperWidth },
                        'relative shrink-0 rounded-xs border text-sm flex justify-center items-center',
                        { 'min-h-[45px] px-4': !getOptionMedia(variantOption) },
                        { 'min-h-[105px] px-1': getOptionMedia(variantOption) },
                        optionStyles(optionState(variantGroup, variantOption), !!getOptionMedia(variantOption))
                    ]"
                    :data-tip="getTooltipText(variantGroup, variantOption)"
                    :tabindex="optionState(variantGroup, variantOption) === 'disabled' ? -1 : 0"
                    data-cy="variant-label"
                    @keydown.enter.prevent="
                        activateNextVariant(index, variantGroup.id);
                        variantGroup?.options && variantGroup?.options?.length > 1 ?
                            selectedOptions[variantGroup.id] === variantOption.id ?
                                deselectOption(variantGroup.id) :
                                selectOption(variantGroup.id, variantOption.id)
                            : null"
                >
                    <MiscImage
                        v-if="getOptionMedia(variantOption)"
                        :src="getOptionMedia(variantOption)!.url"
                        :alt="getOptionMedia(variantOption)!.alt"
                        class="mix-blend-multiply"
                        width="60"
                    />
                    <span v-else>{{ getTranslatedProperty(variantOption, 'name') }}</span>
                    <input
                        v-model="selectedOptions[variantGroup.id]"
                        type="radio"
                        :name="variantGroup.id"
                        :value="variantOption.id"
                        class="hidden radio"
                        :disabled="deactivatedGroups.includes(variantGroup.id)"
                        @click="
                            activateNextVariant(index, variantGroup.id);
                            variantGroup?.options && variantGroup.options.length > 1 ?
                                selectedOptions[variantGroup.id] === variantOption.id ?
                                    deselectOption(variantGroup.id) :
                                    selectOption(variantGroup.id, variantOption.id)
                                : null"
                    >
                </label>
                <button
                    v-if="isGroupCollapsed(variantGroup.id) && getSelectableOptions(variantGroup.options).length * 82 >= variantsWrapperWidth"
                    type="button"
                    class="btn btn-outline border-base-content-subtle bg-base-100 hover:bg-base-200 hover:text-neutral absolute w-[70px] h-full overflow-hidden right-0 top-0 bottom-0 m-auto shadow-[-20px_0px_10px_10px_rgba(255,255,255)] z-10"
                    @click="elapseGroup(variantGroup.id)"
                >
                    + {{ getSelectableOptions(variantGroup.options).length - Math.ceil(variantsWrapperWidth / 82) + 1 }}
                </button>
            </div>
            <LazyProductDetailGroupInfo
                v-if="(variantGroup.translated?.customFields?.custom_property_group_info_trigger ?? variantGroup.customFields?.custom_property_group_info_trigger) != null"
                v-slot="slotProps"
                :property-custom-fields="variantGroup.translated?.customFields ?? variantGroup.customFields"
            >
                <button
                    v-if="(variantGroup.translated?.customFields?.custom_property_group_info_type ?? variantGroup.customFields?.custom_property_group_info_type) === 'modal'"
                    type="button"
                    class="link text-xs text-base-content-subtle -mt-1 text-left"
                    data-cy="size-chart-link"
                    @click.prevent="slotProps.openModal()"
                >
                    <template v-if="(variantGroup.translated?.customFields?.custom_characteristic_size_chart_id ?? variantGroup.customFields?.custom_characteristic_size_chart_id) != null && (variantGroup.translated?.customFields?.custom_characteristic_size_chart_id ?? variantGroup.customFields?.custom_characteristic_size_chart_id)!.trim() !== ''">
                        {{ $t((variantGroup.translated?.customFields?.custom_property_group_info_trigger ?? variantGroup.customFields?.custom_property_group_info_trigger)!) }}
                    </template>
                    <template v-else>
                        {{ variantGroup.translated?.customFields?.custom_property_group_info_trigger ?? variantGroup.customFields?.custom_property_group_info_trigger }}
                    </template>
                </button>
                <MiscLink
                    v-if="(variantGroup.translated?.customFields?.custom_property_group_info_type ?? variantGroup.customFields?.custom_property_group_info_type) === 'link' && (variantGroup.translated?.customFields?.custom_property_group_info_target ?? variantGroup.customFields?.custom_property_group_info_target) != null"
                    :to="(variantGroup.translated?.customFields?.custom_property_group_info_target ?? variantGroup.customFields?.custom_property_group_info_target)!"
                    class="link text-xs text-base-content-subtle -mt-1 text-left"
                >
                    {{ variantGroup.translated?.customFields?.custom_property_group_info_trigger ?? variantGroup.customFields?.custom_property_group_info_trigger }}
                </MiscLink>
            </LazyProductDetailGroupInfo>

            <div v-if="variantGroup.translated?.customFields?.custom_relevant_for_sizekick_toggle ?? variantGroup.customFields?.custom_relevant_for_sizekick_toggle" id="sizekickWidget" />
        </div>
        <div v-if="loadingVariant || loadingOptions || loadingMedia" class="w-full h-full opacity-50 absolute top-0 left-0 bg-base-100 flex justify-center items-center z-10">
            <div class="loading" />
        </div>
        <div v-if="!mounted" class="w-full h-full absolute top-0 left-0 bg-base-100 flex justify-center items-center z-10">
            <div class="loading" />
        </div>
    </div>
    <div
        v-if="shoeSizeProperty"
        class="ShoeSizeMe ssm_pdp"
        :data-shoeid="parentId"
        :data-availability="availableShoeSizeOptionNames"
        :data-sizerun="shoeSizeOptionNames"
    />
</template>

<script setup lang="ts">
import type { Schemas } from "#shopware"
import { useResizeObserver } from '@vueuse/core'
import { getProductUrl, getTranslatedProperty } from '@shopware/helpers'
import { productIncludes } from '~/utils/includes/product'
import type { MiscSelectOption } from '~/components/misc/MiscSelect.vue'

const props = defineProps<{
    variants: Schemas["PropertyGroup"][],
    productId: string,
    parentId?: string,
    preselectOptions?: boolean
}>()

const nuxtApp = useNuxtApp()
const { apiClient } = useShopwareContext()
const { t } = useI18n()
const selectedOptions = ref<Record<string, string>>({})
let previousSelectedOptions: Record<string, string> = {}
const selectableOptions = ref<Record<string, string[]>>({})
const mounted = ref(false)
const loadingVariant = ref(false)
const loadingMedia = ref(false)
const deactivatedGroups = ref<string[]>([])
const onceSelectedGroups = ref<string[]>([])
const lastChangedOptionIsSwatch = ref(false)
// Skip the gallery side effect once for the initial programmatic selection to prevent triggering gallery change depandents like recommendations etc.
let suppressGalleryOnBootstrap = false
const variantsWrapper = ref<HTMLElement>() // used to calculate to show "more" button
const variantsWrapperWidth = ref<0 | number>(0) // used to calculate to show "more" button

// A4G-3329: saleschannels (by app basepath key) that do not rewrite the PDP URL when the
// configuration completes a variant switch. The LT saleschannel is excluded so SEO can check
// whether the "page with redirect" entries in the Search Console are caused by this
// history.replaceState -- for Googlebot it is no redirect, but the reports suggest otherwise.
const VARIANT_URL_UPDATE_EXCLUDED_BASEPATHS = ['lt']

const variantUrlUpdateEnabled = !VARIANT_URL_UPDATE_EXCLUDED_BASEPATHS.includes(
    (nuxtApp.$config?.app?.baseURL ?? '/').replace(/\//g, '').trim().toLowerCase()
)

// Groups with options that have at least one option image defined
// used to collapse / elapse swatch options
const swatchGroups = ref<{ groupId: string, collapsed: boolean }[]>([])
props.variants?.forEach((group) => {
    const hasOptionWithMedia = getSelectableOptions(group?.options).some(option =>
        option.extensions?.configuration?.productConfiguratorMedia != null || option.media != null
    )

    if (hasOptionWithMedia) {
        swatchGroups.value.push({
            groupId: group.id,
            collapsed: true,
        })
    }
})

const { product } = useProduct()
const defaultOptions = computed(() => {
    return product?.value?.optionIds
})

onMounted(async () => {
    if (props.preselectOptions) {
        setDefaultOptions()
        // Only arm if defaults were applied, else the marker never gets consumed
        if (Object.keys(selectedOptions.value).length > 0) {
            suppressGalleryOnBootstrap = true
        }
    } else {
        // Initial state is "no option is selected",
        // but need to render price of variant serverside first
        // and then on clientside, switch allOptionsSelected state of parent component
        await fetchSelectableOptions()
        emit('optionSelected', false)

        // If no default options are preselected, deactivate all groups but the first group
        deactivatedGroups.value = props?.variants?.map(variant => {
            return variant.id
        })
        deactivatedGroups.value.shift()

        // If first group has one option only, select the option and activate next step
        // Otherwise check if first group contains swatches and select the default option
        const firstGroup = props.variants?.[0]
        const firstGroupIsSwatch = swatchGroups.value.find((group) => firstGroup?.id === group.groupId)
        if (firstGroup?.options?.length === 1 && firstGroup?.options[0]) {
            suppressGalleryOnBootstrap = true
            activateNextVariant(0, firstGroup?.id)
            selectOption(firstGroup?.id, firstGroup?.options[0]?.id)
        } else if (firstGroupIsSwatch && firstGroup) {
            // Check if the swatches options is one of the default options
            const defaultOption = firstGroup?.options?.find((option) => defaultOptions?.value?.includes(option.id))
            if (defaultOption) {
                suppressGalleryOnBootstrap = true
                activateNextVariant(0, firstGroup?.id)
                selectOption(firstGroup?.id, defaultOption?.id)
            }
        }
    }

    if (shoeSizeProperty.value && product != null && props?.parentId != null) await initShoeSizeMe(product.value, props?.parentId)

    mounted.value = true
})

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const emit = defineEmits<{
    (e: 'loading', loading: boolean): void,
    // eslint-disable-next-line @typescript-eslint/unified-signatures
    (e: 'optionSelected', allOptionsSelected: boolean): void
}>()
const loadingOptions = ref(false)

const { shoeSizeGroupId, initShoeSizeMe, parseShoeSizeOptionNames } = useShoeSizeMe()
const shoeSizeProperty = ref(props.variants.find((variantGroup) => variantGroup.id === shoeSizeGroupId))
const shoeSizeOptionNames = computed(() => {
    if (shoeSizeProperty?.value?.options != null) {
        return parseShoeSizeOptionNames(shoeSizeProperty?.value?.options)
    }
    return ''
})
const availableShoeSizeOptionNames = ref('')

watch(
    () => selectedOptions.value,
    async (newValue) => {
        // Store previous state separately because newValue and oldValue in deep watchers are always the same
        const oldValues = Object.values(previousSelectedOptions).flat()
        const newValues = Object.values(newValue).flat()

        if (oldValues === newValues) {
            return
        }

        const differences = findArrayDifferences(newValues, oldValues)
        const selectedOption = differences.additions.length > 0
            ? differences.additions[0]
            : differences.removals[0]

        // Check if clicked / changed option is combinable or not
        const selectableOptionIds = Object.values(selectableOptions.value).flat()
        let combinable = false
        if (selectedOption && selectableOptionIds.includes(selectedOption)) {
            combinable = true
        }

        // If changed option is combinable, get selectable options based on current selection
        // If not combinable, get selectable options for the uncombinable option
        // uncombinable options will be deselected in selectableOptions watcher
        if (combinable) {
            await fetchSelectableOptions()
        } else if(selectedOption) {
            await fetchSelectableOptions([selectedOption])
        }

        // Update the previousSelectedOptions for next comparison
        previousSelectedOptions = { ...newValue }

        // Sideeffect: check if selected option is a swatch, if so: fetch and update media gallery data
        if (selectedOption) {
            // Do not update media gallery if watcher triggered by bootstrap selection 
            if (suppressGalleryOnBootstrap) {
                // just once 
                suppressGalleryOnBootstrap = false
            } else {
                lastChangedOptionIsSwatch.value = isSwatchOption(selectedOption)
                updateMediaGallery(selectedOption)
            }
        }
    },
    { deep: true }
)

watch(
    selectableOptions,
    async (value) => {
        // Deselect all not combinable options except for groups with one option only
        const selectableGroupIds = Object.keys(value)
        const selectableOptionIds = Object.values(value).flat()
        Object.keys(selectedOptions.value).forEach((groupId) => {
            const group = props.variants?.find((group) => group.id === groupId)
            const selectedOptionId = selectedOptions.value[groupId]
            if (
                group?.options != null && group?.options?.length > 1 &&
                (
                    !selectableGroupIds.includes(groupId) ||
                    (selectedOptionId != null && !selectableOptionIds.includes(selectedOptionId))
                )
            ) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete selectedOptions.value[groupId]
            }
        })

        /*
         * Automatically change variant when all options are selected
         * Emit event to parent to handle add to cart button state and "from" price rendering
         */
        const attrCount = props.variants?.length
        const selectedCount = Object.keys(selectedOptions.value).length

        // get available shoe sizes for shoesize.me integration container
        if (shoeSizeProperty.value) {
            const availableShoeSizeOptionIds = selectableOptions.value[shoeSizeGroupId]
            const availableShoeSizeOptions = shoeSizeProperty?.value?.options?.filter((option) => {
                return availableShoeSizeOptionIds?.includes(option.id)
            })

            if (availableShoeSizeOptions) {
                availableShoeSizeOptionNames.value = parseShoeSizeOptionNames(availableShoeSizeOptions)
            }
        }

        // all options selected
        if (selectedCount === attrCount) {
            await onVariantChange(selectedOptions.value)
            emit('optionSelected', true)
        } else {
            emit('optionSelected', false)
        }
    },
    { deep: true }
)

useResizeObserver(variantsWrapper, () => {
    variantsWrapperWidth.value = variantsWrapper?.value?.offsetWidth ?? 0
})

function getSelectableOptions (options: Schemas['PropertyGroupOption'][] | null | undefined) {
    return (options ?? []).filter(option =>
        option.extensions?.configuration?.selectable !== false
    )
}

function getOptionMedia (option: Schemas['PropertyGroupOption']) {
    return option.extensions?.configuration?.productConfiguratorMedia ?? option.media ?? null
}

// A group is rendered as a dropdown when its Shopware display type is "Dropdown" (select)
function isSelectGroup (group: Schemas['PropertyGroup']): boolean {
    return group.displayType === 'select'
}

// Whether a group has at least one option with a swatch image (color, material, etc.)
function isSwatchGroup (group: Schemas['PropertyGroup']): boolean {
    return !!swatchGroups.value.find((swatchGroup) => swatchGroup.groupId === group.id)
}

// A text-based group (no swatch images) with only one selectable option is shown as plain text, not a control
function isSingleTextOptionGroup (group: Schemas['PropertyGroup']): boolean {
    return !isSwatchGroup(group) && getSelectableOptions(group.options).length === 1
}

// Map a group's selectable options to MiscSelect options.
// Options are only greyed out / disabled once combinability data has been fetched,
// so preselected variants (no fetch) keep every option selectable.
function getSelectOptions (group: Schemas['PropertyGroup']): MiscSelectOption[] {
    const hasCombinableData = selectableOptions.value[group.id] != null
    return getSelectableOptions(group.options).map((option) => ({
        value: option.id,
        label: option.name ?? '',
        disabled: hasCombinableData && optionState(group, option) === 'uncombinable'
    }))
}

// Select handler for dropdown groups: activate the next group, then apply the selection
// (the selectedOptions watcher takes care of fetching combinable options)
function onSelectChange (groupIndex: number, group: Schemas['PropertyGroup'], optionId: string): void {
    activateNextVariant(groupIndex, group.id)
    selectOption(group.id, optionId)
}

type OptionStates = 'selected' | 'combinable' | 'uncombinable' | 'disabled' | null

function optionStyles (state: OptionStates, media: boolean): string {
    // Swatch options (media) always sit on a grey background, matching the gallery's main product image
    let styles = media ? 'bg-base-200' : ''
    switch (state) {
    case 'selected':
        styles += media
            ? ' border-[#077A50] cursor-pointer'
            : ' text-base-100 border-primary bg-[#077A50] hover:brightness-[110%] cursor-pointer'
        break
    case 'combinable':
        styles += media
            ? ' border-transparent cursor-pointer'
            : ' bg-[#077A50]/8 text-[#077A50] border-[#077A50] hover:bg-[#077A50]/15 cursor-pointer'
        break
    case 'uncombinable':
        styles += media
            ? ' border-transparent cursor-pointer opacity-50 tooltip before:max-w-[100px]'
            : ' bg-base-100 text-base-content-subtle border-base-content-subtle hover:bg-base-200 cursor-pointer tooltip before:max-w-[100px]'
        break
    case 'disabled':
        styles += media
            ? ' border-transparent text-base-content-subtle/30 cursor-not-allowed tooltip before:max-w-[100px]'
            : ' bg-base-100 text-base-content-subtle/30 border-base-content-subtle/30 cursor-not-allowed tooltip before:max-w-[100px]'
        break
    }
    return styles
}

function optionState (group: Schemas['PropertyGroup'], option: Schemas['PropertyGroupOption']): OptionStates {
    const selected = selectedOptions.value[group.id] === option.id
    const combinable = selectableOptions.value[group.id]?.includes(option.id)
    const disabled = deactivatedGroups.value.includes(group.id)

    if (disabled) {
        return 'disabled'
    }

    if (selected) {
        return 'selected'
    }

    if (combinable) {
        return 'combinable'
    }

    if (!combinable && !selected) {
        return 'uncombinable'
    }

    return null
}

function groupStyles (group: Schemas['PropertyGroup']): string {
    const selectedOption = selectedOptions.value[group.id]

    if (selectedOption != null && selectedOption.length > 0) {
        return 'bg-[#077A50] text-primary-content border-[#077A50]'
    }

    return 'border-neutral'
}

// Find next group that is deactivated relative to the selected one and activate it
function activateNextVariant (groupIndex: number, groupId: string): void {
    if (deactivatedGroups.value.length === 0 || onceSelectedGroups.value.includes(groupId)) return

    const groupToActivate = props.variants
        // get all groups after the current index
        .slice(groupIndex + 1)
        // find the first deactivated group
        .find(group => deactivatedGroups.value.includes(group.id))

    if (groupToActivate) {
        deactivatedGroups.value = deactivatedGroups.value
            // remove the matching ID if found
            .filter(id => id !== groupToActivate.id)

        // If newly activated group has one option only, select that option and activate the next group (recursion)
        if (groupToActivate?.options?.length === 1) {
            const groupToActivateIndex = props.variants.findIndex(group => group.id === groupToActivate.id)
            const singleOption = groupToActivate.options[0]
            if (singleOption?.id) {
                activateNextVariant(groupToActivateIndex, groupToActivate.id)
                selectOption(groupToActivate.id, singleOption.id)
            }
        }
    }
}

function getTooltipText (group: Schemas['PropertyGroup'], option: Schemas['PropertyGroupOption']): string {
    let text = ''
    const state = optionState(group, option)

    switch (state) {
    case 'uncombinable': {
        text = t('product.detail.variants.tooltipOptionUncombinable')
        break
    }
    case 'disabled': {
        // Get all groups that are not selected AND active AND have at least one option
        const groups = props.variants?.filter((variantGroup) => {
            return !(variantGroup.id in selectedOptions.value) && !deactivatedGroups.value.includes(variantGroup.id)
        })
        const groupNames = Array.from(groups, (group) => group.name)
        text = t('product.detail.variants.tooltipOptionDisabled', { groups: groupNames.join(' & ')})
        break
    }}

    return text
}

async function searchProductVariantIds (productId: string, options: | string[] | { [key: string]: string; }) {
    return await apiClient.invoke('searchProductVariantIds post /product/{productId}/find-variant', {
        pathParams: { 
            productId
        },
        body: {
            options
        }
    })
}

async function readProductDetail (productId: string) {
    return await apiClient.invoke('readProductDetail post /product/{productId}', {
        pathParams: {
            productId
        },
        body: {
            associations: {
                crossSellings: {},
                deliveryTime: {},
                media: {
                    associations: {
                        media: {}
                    }
                },
                cover: {
                    associations: {
                        media: {}
                    }
                },
                manufacturer: {
                    associations: {
                        media: {}
                    }
                },
                seoUrls: {}
            },
            includes: productIncludes,
            customFieldsMedia: {
                greenfeed_link_image: {}
            },
        }
    })
}

async function onVariantChange (options: Record<string, string>) {
    loadingVariant.value = true
    emit('loading', true)
    const { data: findVariantResponse } = await searchProductVariantIds(props.parentId != null ? props.parentId : props.productId, options)

    if (!findVariantResponse.variantId) {
        console.error( 'No matching variant found')
        return
    }

    const { data: productResponse } = await readProductDetail(findVariantResponse.variantId)

    if (!productResponse.product) {
        console.error('No matching variant found')
    }

    const variant = productResponse.product

    loadingVariant.value = false
    emit('loading', false)

    // Emit global event
    // changedGroupIsSwatch: whether the option that just completed the configuration is a
    // swatch option (e.g. color). Consumers that only care about color changes (like 8select
    // recommendations) use it, since updateMediaGallery's productGalleryDataChanged is skipped
    // in this case (loadingVariant is true while this full variant switch is in flight).
    nuxtApp.callHook('productVariantChanged', {
        data: variant,
        configurator: productResponse.configurator ? unref(productResponse.configurator) : undefined,
        changedGroupIsSwatch: lastChangedOptionIsSwatch.value
    })

    // Replace current url path with variant without losing optional GET params or language path.
    // formatLink() already applies the locale prefix per the i18n strategy, so we only prepend baseURL.
    // Skipped on the saleschannels listed in VARIANT_URL_UPDATE_EXCLUDED_BASEPATHS (A4G-3329).
    if (variantUrlUpdateEnabled) {
        const variantUrl = formatLink(getProductUrl(variant))
        const newUrl = window.location.href.replace(
            window.location.pathname,
            `${nuxtApp.$config?.app?.baseURL}${variantUrl}`.replace(/\/+/g, '/')
        )
        window.history.replaceState({}, variant.name, newUrl)
    }

    // getProductVariant (/find-variant) responds with the closest variant found, not necessary the exact one
    // therefore, the options of the found variant must be compared to the ones that are currently selected in the interface
    // deselect the options, which are not present in the current products options 
    if (variant?.optionIds != null && variant.optionIds.length > 0) {
        const missingOptions = findMissingOptions(variant.optionIds, selectedOptions.value)
        Object.keys(missingOptions).map((groupId) => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete selectedOptions.value[groupId]
        })
    }
}

async function fetchSelectableOptions (manualOptions?: string[]) {
    loadingOptions.value = true
    try {
        const options = manualOptions != null ? manualOptions : Object.keys(selectedOptions.value).map((key: string) => {
            return selectedOptions?.value?.[key] != null ? selectedOptions?.value?.[key] : false
        })

        if(!props.parentId) {
            loadingOptions.value = false
            return
        }

        const { data: res } = await apiClient.invoke('readCombinableOptions post /product/{productId}/combinable-options', {
            pathParams: {
                productId: props.parentId
            },
            body: {
                options
            }
        })

        if (res != null) {
            const { apiAlias, ...ids } = res
            selectableOptions.value = ids
        }
    } catch (e) {
        console.error(e)
    }
    loadingOptions.value = false
}

function selectOption (groupId: string, optionId: string) {
    selectedOptions.value[groupId] = optionId
    if (!onceSelectedGroups.value.includes(groupId)) onceSelectedGroups.value.push(groupId)
}

function deselectOption (variantGroupId: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete selectedOptions.value[variantGroupId]
}

function setDefaultOptions () {
    props.variants?.forEach((group) => {
        group?.options?.forEach((option) => {
            if (defaultOptions?.value?.includes(option.id)) {
                selectedOptions.value[group.id] = option.id
            }
        })
    })
}

// Function to find differences between two arrays
function findArrayDifferences (array1: string[], array2: string[]) {
    // Convert arrays to Sets for O(1) lookup
    const set1 = new Set(array1)
    const set2 = new Set(array2)

    // Find items in array1 that aren't in array2
    const additions = array1.filter(item => !set2.has(item))

    // Find items in array2 that aren't in array1
    const removals = array2.filter(item => !set1.has(item))

    return {
        additions,
        removals
    }
}

function isGroupCollapsed(groupId: string): boolean {
    const group = swatchGroups.value.find(group => group.groupId === groupId)
    return !!group && group.collapsed
}

function elapseGroup(groupId: string): void {
    const group = swatchGroups.value.find(group => group.groupId === groupId)
    if (group) group.collapsed = false
}

// Whether an option is rendered as an image swatch (e.g. color) rather than e.g. size
function isSwatchOption (optionId: string): boolean {
    return !!props.variants.find((variant) => {
        return variant?.options?.find((option) => {
            return option?.id === optionId
                && (option.extensions?.configuration?.productConfiguratorMedia != null || option.media != null)
        })
    })
}

async function updateMediaGallery (selectedOptionId: string) {
    if (isSwatchOption(selectedOptionId) && !loadingVariant.value) {
        // find variant of current selection
        try {
            loadingMedia.value = true
            const { data: findVarianResponse } = await searchProductVariantIds(props.parentId != null ? props.parentId : props.productId, selectedOptions.value)

            if (!findVarianResponse?.variantId) {
                console.error( 'No matching variant found')
                return
            }

            const { data: productResponse } = await readProductDetail(findVarianResponse.variantId)

            if (!productResponse.product) {
                console.error('No matching variant found')
            }

            const variant = productResponse.product

            nuxtApp.callHook('productGalleryDataChanged', { data: variant })
            loadingMedia.value = false
        } catch (e) {
            console.error(e)
        } finally {
            loadingMedia.value = false
        }
    }
}
 
// Returns key-value pairs from an object where values are not present in the provided array
function findMissingOptions(valuesArray: string[], objectWithValues: Record<string, string>): Record<string, string> {
    const missingPairs: Record<string, string> = {}
    
    for (const [key, value] of Object.entries(objectWithValues)) {
        if (!valuesArray.includes(value)) {
            missingPairs[key] = value
        }
    }
    
    return missingPairs
}
</script>
